var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo } from 'react';
import { GLYPHS } from 'frontend/js/app/common/icon';
import { useBreakpoints } from '../../../infra/dls/hooks/use-get-breakpoints';
import { EngageTracker } from '../tracking';
import { ENGAGE_GEN_AI_SUGGESTION_CLICKED } from '../tracking/events';
import { AIPopover } from './ai-popover';
export var AIPopoverContainer = function (_a) {
    var onClose = _a.onClose, onRephraseClick = _a.onRephraseClick, onMoreFormalClick = _a.onMoreFormalClick, onMorePlayfulClick = _a.onMorePlayfulClick, onShortenClick = _a.onShortenClick, onElaborateClick = _a.onElaborateClick, onPromptSubmit = _a.onPromptSubmit, _b = _a.isLoading, isLoading = _b === void 0 ? false : _b, _c = _a.isFetchingSuggestions, isFetchingSuggestions = _c === void 0 ? false : _c, currentAction = _a.currentAction, autoFocusPrompt = _a.autoFocusPrompt, _d = _a.dynamicSuggestions, dynamicSuggestions = _d === void 0 ? [] : _d, _e = _a.suggestionsAreDirectContent, suggestionsAreDirectContent = _e === void 0 ? false : _e, onInsertContent = _a.onInsertContent, onUndoClick = _a.onUndoClick, _f = _a.hasUndoContent, hasUndoContent = _f === void 0 ? false : _f, onGenerateSimilarClick = _a.onGenerateSimilarClick, _g = _a.type, type = _g === void 0 ? 'text' : _g, isEmpty = _a.isEmpty, _h = _a.isShortenAllowed, isShortenAllowed = _h === void 0 ? false : _h, section = _a.section;
    var _j = React.useState(''), customPrompt = _j[0], setCustomPrompt = _j[1];
    var isMobileOrSmaller = useBreakpoints().isMobileOrSmaller;
    var promptInputRef = React.useRef(null);
    var popoverRef = React.useRef(null);
    var focusPrompt = useCallback(function () {
        var _a;
        if (autoFocusPrompt) {
            (_a = promptInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [autoFocusPrompt]);
    useEffect(function () {
        var checkIfClickedOutside = function () {
            focusPrompt();
        };
        var currentRef = popoverRef.current;
        currentRef === null || currentRef === void 0 ? void 0 : currentRef.addEventListener('click', checkIfClickedOutside);
        return function () {
            currentRef === null || currentRef === void 0 ? void 0 : currentRef.removeEventListener('click', checkIfClickedOutside);
        };
    }, [focusPrompt]);
    var handleClose = function () {
        onClose();
    };
    var handlePromptChange = function (e) {
        setCustomPrompt(e.target.value);
    };
    var handlePromptSubmit = function () {
        if (customPrompt.trim()) {
            onPromptSubmit(customPrompt);
            setCustomPrompt('');
        }
    };
    var handleKeyDown = function (e) {
        if (e.key === 'Enter') {
            handlePromptSubmit();
        }
    };
    var trackSuggestionClick = useCallback(function (label, source, directInsert) {
        if (source === void 0) { source = 'preset'; }
        if (directInsert === void 0) { directInsert = false; }
        EngageTracker.getInstance().track(ENGAGE_GEN_AI_SUGGESTION_CLICKED, {
            section: label === 'Generate similar' ? section || (type === 'image' ? 'image' : undefined) : section,
            source: source,
            directInsert: directInsert,
            label: label,
        });
    }, [section, type]);
    // Base hardcoded suggestions - for images, we only show these when there's an existing image
    var baseSuggestions = useMemo(function () {
        return onGenerateSimilarClick
            ? [
                // For images: only show Generate Similar when there's an existing image
                {
                    label: 'Generate similar',
                    icon: GLYPHS.refresh,
                    onClick: function () {
                        trackSuggestionClick('Generate similar');
                        onGenerateSimilarClick === null || onGenerateSimilarClick === void 0 ? void 0 : onGenerateSimilarClick();
                    },
                },
            ]
            : __spreadArray(__spreadArray([
                // For text: show the standard text editing options
                {
                    label: 'Rephrase',
                    icon: GLYPHS.rewrite,
                    onClick: function () {
                        trackSuggestionClick('Rephrase');
                        onRephraseClick();
                    },
                },
                {
                    label: 'More formal',
                    icon: GLYPHS.rewrite,
                    onClick: function () {
                        trackSuggestionClick('More formal');
                        onMoreFormalClick();
                    },
                },
                {
                    label: 'More playful',
                    icon: GLYPHS.rewrite,
                    onClick: function () {
                        trackSuggestionClick('More playful');
                        onMorePlayfulClick();
                    },
                }
            ], (isShortenAllowed
                ? [
                    {
                        label: 'Shorten',
                        icon: GLYPHS.textAlignMiddle,
                        onClick: function () {
                            trackSuggestionClick('Shorten');
                            onShortenClick();
                        },
                    },
                ]
                : []), true), [
                {
                    label: 'Elaborate',
                    icon: GLYPHS.textParagraphSpacing,
                    onClick: function () {
                        trackSuggestionClick('Elaborate');
                        onElaborateClick();
                    },
                },
            ], false);
    }, [
        onGenerateSimilarClick,
        onRephraseClick,
        onMoreFormalClick,
        onMorePlayfulClick,
        isShortenAllowed,
        onShortenClick,
        onElaborateClick,
        trackSuggestionClick,
    ]);
    // Use dynamic suggestions if available, otherwise fall back to hardcoded suggestions
    var suggestions = useMemo(function () {
        return dynamicSuggestions.length > 0
            ? dynamicSuggestions.map(function (suggestion) { return ({
                label: suggestion.label,
                icon: GLYPHS.rewrite,
                onClick: function () {
                    // Handle based on whether suggestions are direct content or prompts
                    if (suggestionsAreDirectContent && onInsertContent) {
                        trackSuggestionClick(suggestion.label, 'api', true);
                        onInsertContent(suggestion.prompt); // Insert content directly
                    }
                    else {
                        trackSuggestionClick(suggestion.label, 'api');
                        onPromptSubmit(suggestion.prompt); // Send as refine prompt
                    }
                },
            }); })
            : baseSuggestions;
    }, [
        dynamicSuggestions,
        suggestionsAreDirectContent,
        onInsertContent,
        onPromptSubmit,
        baseSuggestions,
        trackSuggestionClick,
    ]);
    // Add undo button at the beginning if there's content to undo
    var allSuggestions = hasUndoContent && onUndoClick
        ? __spreadArray([
            {
                label: 'Undo',
                icon: GLYPHS.rename,
                iconSize: 'medium',
                onClick: onUndoClick,
            }
        ], suggestions, true) : suggestions;
    // Add Done button as first suggestion on mobile (after undo if present)
    var mobileSuggestions = __spreadArray([
        {
            label: 'Done',
            icon: GLYPHS.checkMark,
            iconSize: 'small',
            onClick: handleClose,
        }
    ], allSuggestions, true);
    var suggestionsToShow = (isMobileOrSmaller ? mobileSuggestions : allSuggestions);
    var popoverHeader = useMemo(function () {
        if (isLoading)
            return "Generating ".concat(type);
        if (!isMobileOrSmaller)
            return 'Suggestions';
        return '';
    }, [isLoading, type, isMobileOrSmaller]);
    return (_jsx(AIPopover, { popoverRef: popoverRef, promptInputRef: promptInputRef, popoverHeader: popoverHeader, isLoading: isLoading, isFetchingSuggestions: isFetchingSuggestions, currentAction: currentAction, suggestionsToShow: suggestionsToShow, customPrompt: customPrompt, onClose: handleClose, onPromptChange: handlePromptChange, onPromptSubmit: handlePromptSubmit, onKeyDown: handleKeyDown, autoFocusPrompt: autoFocusPrompt, type: type, isEmpty: isEmpty, isMobileOrSmaller: isMobileOrSmaller }));
};
